<template>
  <!-- PHONE NUMBER INPUTS -->
  <b-form-row class="mt-2">
    <div class="col-12">
      <div class="row align-items-center mb-2">
        <div class="col-6">
          <label class="hot-edit-label">Phone Numbers</label>
        </div>
        <div class="col-6 text-right">
          <div v-if="editMode && localPhoneNumberCount">
            <span
              v-if="allNumbersValid"
              key="validBadge"
              class="chip chip-success"
            >
              <b-icon
                icon="check2-circle"
                class="mr-1"
              ></b-icon>
              OK
            </span>
            <span
              v-else
              key="invalidBadge"
              class="chip chip-danger"
            >
              <b-icon
                icon="exclamation-circle"
                class="mr-1"
              ></b-icon>
              Errors
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="localPhoneNumberCount || editMode"
      class="col-12"
    >
      <div class="d-flex flex-column gap-2">
        <div
          v-for="item in Object.values(phoneNumberStore)"
          :key="item._localId"
        >
          <!-- the ref will be an array since its in a loop -->
          <BootstrapPhoneNumberInput
            ref="phoneNumberInputs"
            :phone-data="item"
            :countries="allCountries"
            :preferred-country-code="preferredCountryCode"
            @update="handleNumberUpdated(item._localId, ...arguments)"
            @validation="handleValidationEvent(item._localId, ...arguments)"
            @remove="removePhone(item._localId)"
            :edit-mode="editMode"
          ></BootstrapPhoneNumberInput>
        </div>
      </div>
      <div class="mt-1">
        <b-button
          v-if="editMode"
          variant="gray-primary"
          size="sm"
          @click="addNewPhone"
        >
          + Add Phone Number
        </b-button>
      </div>
    </div>
    <div
      v-else
      class="col-12"
    >
      <div class="text-muted mb-0 font-weight-400 font-size-70">No phone numbers</div>
    </div>
  </b-form-row>
  <!-- END OF PHONE NUMBER INPUTS -->
</template>

<script>
  import { suppliosApi } from '@/js/api-service';
  import { nanoid } from 'nanoid';
  // import cloneDeep from 'lodash/cloneDeep';
  import sortBy from 'lodash/sortBy';
  import merge from 'lodash/merge';
  // import { AsYouType, parsePhoneNumber } from 'libphonenumber-js/min';
  import BootstrapPhoneNumberInput from '@/components/shared/BootstrapPhoneNumberInput.vue';

  // ! DO NOT USE THE INITIAL PHONE NUMBERS PROP TO UPDATE THE PHONE NUMBERS AFTER THE COMPONENET MOUNTED
  // ! USE THE setPhoneNumbers METHOD TO UPDATE THE PHONE NUMBERS AFTER THE COMPONENT MOUNTED

  // NOTE: we do not do validation in here anymore... we do that in the child component
  // so if we pass in invalid phone numbers, they will be shown as invalid in child, but child won't emit them here to override what we have here

  // NOTE: when a number is removed, it seems the ref/component stays around longer than the data, so maybe the registry should hold the refs, not the data? then we immediately remove the ref from registry, and can run validation on the right things?

  export default {
    name: 'TuPhoneNumbers',
    components: {
      BootstrapPhoneNumberInput,
    },
    mixins: [],
    props: {
      // the phone numbers to display
      initialPhoneNumbers: {
        type: Array,
        required: true,
      },
      // if the form should show up in edit mode
      editMode: {
        type: Boolean,
        required: true,
      },
      // The company ID to pass to the loadAddressFormData API call
      // this is used to fetch the grouped sorted list of countries for the phone number input
      companyId: {
        type: String,
        required: false,
      },
    },
    computed: {
      localPhoneNumberCount() {
        return Object.values(this.phoneNumberStore).length;
      },
      allNumbersValid() {
        return Object.values(this.phoneNumberStore).every((p) => p._isValid);
      },
    },
    data() {
      return {
        // holds the countries fetched from the backend
        allCountries: [],
        // holds the phone numbers of the user
        phoneNumberStore: {}, // keyed by _localId
        preferredCountryCode: undefined, // will be used to set the default country code in the phone number input
      };
    },
    watch: {
      companyId: {
        handler() {
          // if the company ID changes (even undefined), we need to fetch the countries
          this.fetchCountries();
        },
        immediate: true,
      },
    },
    mounted() {
      // this.localPhoneNumbers = this.initialPhoneNumbers;
      if (this.initialPhoneNumbers.length) {
        // if we have initial phone numbers, we will not make an empty one, even if we are in edit mode
        // but if NO phone numbers, we will add one empty if we are in edit mode
        this.setPhoneNumbers(this.initialPhoneNumbers);
      }
    },
    methods: {
      // PUBLIC METHODS
      /**
       * To set the phone numbers from the parent component
       * Initial phone numbers will not listen to changes
       * Will use this data to populate the store
       * @param phoneNumbersArray - an array of phone number objects, each with a name, country, number, extension, and position
       */
      setPhoneNumbers(phoneNumbersArray) {
        const numArray = Array.isArray(phoneNumbersArray) ? phoneNumbersArray : [];
        const phoneStore = {};
        numArray.forEach((phoneData, index) => {
          // since these are coming from the parent, we will assume they are valid numbers to start with
          const localNumber = this.makeLocalNumber({ ...phoneData, position: index }, true); // overwrite the position with the current index
          phoneStore[localNumber._localId] = localNumber;
        });
        this.phoneNumberStore = phoneStore; // overwrite the entire thing at once
      },
      /**
       * For the parent component to decide if all phone numbers are valid or not
       * Checks if all phone numbers are valid
       */
      isAllValid() {
        // previously we got all phone number input components and called their isValid method
        // but now we have validation status here, we just use that
        // NOTE - this will block the form from submitting if there are any invalid phone numbers
        // if you don't want that, then either don't check this, since this only emits VALID numbers
        return Object.values(this.phoneNumberStore).every((p) => p._isValid);
      },

      // PRIVATE / INTERNAL METHODS
      /**
       * Fetches the countries from django countries package
       */
      async fetchCountries() {
        try {
          const response = await suppliosApi.locations.loadAddressFormData({
            params: {
              company_id: this.companyId,
            },
          });
          this.allCountries = response.data.grouped_countries || response.data.countries || [];
          this.preferredCountryCode = response.data.preferred_country_code || '';
        } catch (error) {
          this.$toast.error("Error while loading the user's phone number!");
        }
      },
      getAllInputComponents() {
        return this.$refs.phoneNumberInputs || [];
      },
      /**
       * handle input event from a single phone number input
       */
      handleNumberUpdated(_localId, numberData, isValid) {
        console.log('handleNumberUpdated in TuPhoneNumbers', _localId, numberData, isValid);
        // we merge the payload with the current phone number
        const currentNumber = this.phoneNumberStore[_localId];
        const newNumber = merge({}, currentNumber, numberData, { _isValid: isValid }); // must make sure to override the _isValid value LAST
        this.$set(this.phoneNumberStore, _localId, newNumber);
        this.emitNumbers();
      },
      handleValidationEvent(_localId, isValid) {
        console.log('handleValidationEvent in TuPhoneNumbers', _localId, isValid);
        // do NOT do a full merge of the payload... or else it updates prop of child components, and it will blank stuff out
        const currentNumber = this.phoneNumberStore[_localId];
        // const newNumber = merge({}, currentNumber, { _isValid: isValid }); // must make sure to override the _isValid value LAST
        this.$set(currentNumber, '_isValid', isValid);  // ONLY update the _isValid flag
        // do we need to emitNumbers here also?  if so, be careful not to pass things up, have parent pass new ones back down, and we mess up children components
      },
      /**
       * Emits a new phone number list but only the ones that are valid
       * Also validates them - puts errors in the local errors object
       */
      emitNumbers() {
        // emit ONLY the valid numbers, and sort them by position
        const validNumbers = Object.values(this.phoneNumberStore).filter((p) => p._isValid);
        const orderedNumbers = sortBy(validNumbers, ['position']);
        // strip off _localId?  so we set a new one if we get the object passed back in?
        // const preppedNumbers = orderedNumbers.map(p => {
        //     const { _localId, ...rest } = p;
        //     return rest;
        // });
        this.$emit('changed', orderedNumbers);
      },
      makeLocalNumber(phoneData, isValid) {
        // don't add position here -- because we want to call this when building up initial list also, we don't know all the positions yet
        // but you CAN add position in phoneData you pass in (like when making initial list, or when adding a new one)
        // also OPTIONALLY set isValid, if you know it, will default to false
        return {
          ...phoneData,
          _localId: nanoid(),
          _isValid: isValid ?? false, // then have child component emit on ALL changes, and we can know/show validation here
          // TODO - add _editMode so you can make them individually editable -- but only when you have endpoints for single ones
        };
      },
      addNewPhone() {
        const newNumber = this.makeLocalNumber(
          {
            name: '',
            country: this.preferredCountryCode,
            number: '',
            extension: '',
            position: Object.values(this.phoneNumberStore).length, // overwrite the position with the current length of the store
          },
          false,
        ); // set isValid to false here because we know it is blank, invalid
        this.$set(this.phoneNumberStore, newNumber._localId, newNumber);
      },
      /**
       * Removes a phone number from the local list
       * Emits a changed event
       * @param _localId
       */
      removePhone(_localId) {
        this.$delete(this.phoneNumberStore, _localId);
        this.emitNumbers();
      },
    },
  };
</script>
